import { IApiError } from '../../../models/api';
import {
  IClosetWithSite,
  ISite,
  ISiteType,
  ISubSiteType,
  IUpdateClosetWithSite,
} from '../../../models/site';
import { ApiService } from '../../../services/api';
import { CONSTANTS } from '../../../utils/constants';
import { getErrorMessages } from '../../../utils/formatters';
import { generateAsyncActionNames } from '../../../utils/helpers';
import { AppDispatch } from '../../type';
import { TableActions } from '../table/table.actions';
import {
  AddLockParams,
  AddLockPayload,
  ChangeLockStatusParams,
  ChangeLockStatusPayload,
  GetLocksPayload,
  LocksState,
  ReleaseLockPayload,
  ReleaseMigrationLockParams,
} from './type';

const { get, post, put } = ApiService;

enum LocksActionsEnum {
  GET_LOCKS = 'GET_LOCKS',
  RELEASE_MIGRATION_LOCK = 'RELEASE_MIGRATION_LOCK',
  ADD_LOCK = 'ADD_LOCK',
  CHANGE_LOCK_STATUS = 'CHANGE_LOCK_STATUS',
}

export const LocksActionsNames = {
  [LocksActionsEnum.GET_LOCKS]: generateAsyncActionNames(
    LocksActionsEnum.GET_LOCKS
  ),
  [LocksActionsEnum.RELEASE_MIGRATION_LOCK]: generateAsyncActionNames(
    LocksActionsEnum.RELEASE_MIGRATION_LOCK
  ),
  [LocksActionsEnum.ADD_LOCK]: generateAsyncActionNames(
    LocksActionsEnum.ADD_LOCK
  ),
  [LocksActionsEnum.CHANGE_LOCK_STATUS]: generateAsyncActionNames(
    LocksActionsEnum.CHANGE_LOCK_STATUS
  ),
};

const Actions = {
  [LocksActionsEnum.GET_LOCKS]: {
    START: () => ({
      type: LocksActionsNames.GET_LOCKS.START,
    }),
    FULFILLED: (data: GetLocksPayload) => ({
      type: LocksActionsNames.GET_LOCKS.FULFILLED,
      payload: data,
    }),
    REJECTED: (error: IApiError[] | null) => ({
      type: LocksActionsNames.GET_LOCKS.REJECTED,
      payload: error,
    }),
  },
  [LocksActionsEnum.RELEASE_MIGRATION_LOCK]: {
    START: () => ({
      type: LocksActionsNames.RELEASE_MIGRATION_LOCK.START,
    }),
    FULFILLED: (data: ReleaseLockPayload) => ({
      type: LocksActionsNames.RELEASE_MIGRATION_LOCK.FULFILLED,
      payload: data,
    }),
    REJECTED: (error: IApiError[] | null) => ({
      type: LocksActionsNames.RELEASE_MIGRATION_LOCK.REJECTED,
      payload: error,
    }),
  },
  [LocksActionsEnum.ADD_LOCK]: {
    START: () => ({
      type: LocksActionsNames.ADD_LOCK.START,
    }),
    FULFILLED: (data: AddLockPayload) => ({
      type: LocksActionsNames.ADD_LOCK.FULFILLED,
      payload: data,
    }),
    REJECTED: (error: IApiError[] | null) => ({
      type: LocksActionsNames.ADD_LOCK.REJECTED,
      payload: error,
    }),
  },
  [LocksActionsEnum.CHANGE_LOCK_STATUS]: {
    START: () => ({
      type: LocksActionsNames.CHANGE_LOCK_STATUS.START,
    }),
    FULFILLED: (data: ChangeLockStatusPayload) => ({
      type: LocksActionsNames.CHANGE_LOCK_STATUS.FULFILLED,
      payload: data,
    }),
    REJECTED: (error: IApiError[] | null) => ({
      type: LocksActionsNames.CHANGE_LOCK_STATUS.REJECTED,
      payload: error,
    }),
  },
};

const getLocks =
  (
    params?: any,
    sort?: any,
    updateTable = true,
    pageNumber: number = CONSTANTS.GRIDS.FIRST_PAGE_NUMBER,
    pageSize?: number | null
  ) =>
  async (dispatch: AppDispatch, getState: any) => {
    dispatch(Actions[LocksActionsEnum.GET_LOCKS].START());

    const requestData = {
      params,
      headers: {
        'X-Pagination': JSON.stringify({
          PageNumber: pageNumber,
          PageSize:
            pageSize ?? getState()?.table.pageSize ?? CONSTANTS.GRIDS.PAGE_SIZE,
        }),
      },
    };

    if (sort && sort.length) {
      (requestData.headers as any)['X-Sorting'] = JSON.stringify({
        Sortings: sort,
      });
    }

    try {
      const { data }: any = await get('Lock/GetLocks', requestData);

      const locks = data?.content?.locks;

      dispatch(Actions[LocksActionsEnum.GET_LOCKS].FULFILLED(locks));

      if (updateTable) {
        (dispatch as any)(
          TableActions.setTableData({
            totalRows: data?.content?.totalCount,
            pageNumber: CONSTANTS.GRIDS.FIRST_PAGE_NUMBER,
          })
        );
      }
      return locks;
    } catch (error: any) {
      dispatch(
        Actions[LocksActionsEnum.GET_LOCKS].REJECTED(getErrorMessages(error))
      );
    }
  };

const releaseMigrationLock =
  (params: ReleaseMigrationLockParams) => async (dispatch: AppDispatch) => {
    dispatch(Actions[LocksActionsEnum.RELEASE_MIGRATION_LOCK].START());
    try {
      const requestBody = {
        CaseId: params.CaseId,
        IdentificationNumber: params.IdentificationNumber,
      };

      const response = await post('/Migration/ReleaseMigration', requestBody);
      if (!response?.data.isSucceeded) {
        throw Error(JSON.stringify(response.data.errors));
      }
      dispatch(
        Actions[LocksActionsEnum.RELEASE_MIGRATION_LOCK].FULFILLED(response)
      );
      return response;
    } catch (error: any) {
      dispatch(
        Actions[LocksActionsEnum.RELEASE_MIGRATION_LOCK].REJECTED(
          getErrorMessages(error)
        )
      );
    }
  };

const addLock = (params: AddLockParams) => async (dispatch: AppDispatch) => {
  dispatch(Actions[LocksActionsEnum.ADD_LOCK].START());
  params.PemType = +params.PemType;
  try {
    const response = await post('/Lock/AddLock', params);
    if (!response?.data.isSucceeded) {
      throw Error(JSON.stringify(response.data.errors));
    }
    dispatch(Actions[LocksActionsEnum.ADD_LOCK].FULFILLED(response));
    return response;
  } catch (error: any) {
    dispatch(
      Actions[LocksActionsEnum.ADD_LOCK].REJECTED(getErrorMessages(error))
    );
  }
};

const changeLockStatus =
  (params: ChangeLockStatusParams) => async (dispatch: AppDispatch) => {
    dispatch(Actions[LocksActionsEnum.CHANGE_LOCK_STATUS].START());
    try {
      const response = await put('/Lock/ChangeLockStatus', params);
      if (!response?.data.isSucceeded) {
        throw Error(JSON.stringify(response.data.errors));
      }
      dispatch(
        Actions[LocksActionsEnum.CHANGE_LOCK_STATUS].FULFILLED(
          response.data.content
        )
      );
      return response;
    } catch (error: any) {
      dispatch(
        Actions[LocksActionsEnum.CHANGE_LOCK_STATUS].REJECTED(
          getErrorMessages(error)
        )
      );
    }
  };

export const LocksActions = {
  getLocks,
  releaseMigrationLock,
  addLock,
  changeLockStatus,
};
